import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";

import { auth, db } from "../../firebase/firebase";
import { sendMessageToTherapist } from "../../services/therapistClient";
import { fetchTtsUrl } from "../../utils/ttsBackend";

const sanitizeQuickReplyOptions = (options = []) => {
  if (!Array.isArray(options)) {
    return [];
  }
  const unique = [];
  for (const option of options) {
    const trimmed = typeof option === "string" ? option.trim() : "";
    if (!trimmed) {
      continue;
    }
    if (unique.some((entry) => entry.toLowerCase() === trimmed.toLowerCase())) {
      continue;
    }
    unique.push(trimmed);
    if (unique.length >= 4) {
      break;
    }
  }
  return unique;
};

const TherapistPage = () => {
  const theme = useTheme();
  const [uid, setUid] = useState(auth.currentUser?.uid || null);
  const [messages, setMessages] = useState([]);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [incomingSuggestions, setIncomingSuggestions] = useState([]);
  const [playingMessageId, setPlayingMessageId] = useState(null);
  const [loadingMessageId, setLoadingMessageId] = useState(null);
  const listRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (uid) {
      return undefined;
    }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid || null);
    });
    return unsubscribe;
  }, [uid]);

  useEffect(() => {
    if (!uid) {
      setMessages([]);
      return undefined;
    }

    const messagesRef = collection(db, "users", uid, "therapistMessages");
    const messagesQuery = query(messagesRef, orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const docs = snapshot.docs.map((doc) => {
        const data = doc.data();
        let timestamp = null;
        if (data.timestamp?.toDate) {
          timestamp = data.timestamp.toDate();
        } else if (data.timestamp instanceof Date) {
          timestamp = data.timestamp;
        } else if (typeof data.timestamp === "string") {
          const parsed = new Date(data.timestamp);
          timestamp = Number.isNaN(parsed.getTime()) ? null : parsed;
        }
        return {
          id: doc.id,
          ...data,
          timestamp,
        };
      });
      setMessages(docs);
    });

    return unsubscribe;
  }, [uid]);

  useEffect(() => {
    if (!listRef.current) {
      return;
    }
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, pendingMessages]);

  const combinedMessages = useMemo(() => {
    if (!pendingMessages.length) {
      return messages;
    }
    return [...messages, ...pendingMessages];
  }, [messages, pendingMessages]);

  useEffect(() => {
    if (!incomingSuggestions.length) {
      return;
    }
    for (let index = combinedMessages.length - 1; index >= 0; index -= 1) {
      const message = combinedMessages[index];
      if (!message || message.role !== "assistant" || message.pending) {
        continue;
      }
      const options =
        Array.isArray(message.suggestedReplies) && message.suggestedReplies.length
          ? message.suggestedReplies
          : message.metadata?.suggestedReplies;
      if (sanitizeQuickReplyOptions(options).length) {
        setIncomingSuggestions([]);
      }
      break;
    }
  }, [combinedMessages, incomingSuggestions, setIncomingSuggestions]);

  const quickReplies = useMemo(() => {
    if (incomingSuggestions.length) {
      return incomingSuggestions;
    }
    for (let index = combinedMessages.length - 1; index >= 0; index -= 1) {
      const message = combinedMessages[index];
      if (!message || message.role !== "assistant" || message.pending) {
        continue;
      }
      const options =
        Array.isArray(message.suggestedReplies) && message.suggestedReplies.length
          ? message.suggestedReplies
          : message.metadata?.suggestedReplies;
      const sanitized = sanitizeQuickReplyOptions(options);
      if (sanitized.length) {
        return sanitized;
      }
    }
    return [];
  }, [combinedMessages, incomingSuggestions]);

  useEffect(
    () => () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    },
    []
  );

  const accentTone = useMemo(
    () => theme.palette.secondary?.main ?? theme.palette.primary.light ?? theme.palette.primary.main,
    [theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary?.main]
  );

  const sendChatMessage = useCallback(
    async (messageText, { preserveDraft = false } = {}) => {
      const trimmed = messageText.trim();
      if (!trimmed || sending || !uid) {
        return false;
      }

      setError(null);
      setIncomingSuggestions([]);
      const pendingEntry = {
        id: `pending-${Date.now()}`,
        role: "user",
        content: trimmed,
        timestamp: new Date(),
        pending: true,
      };
      setPendingMessages((prev) => [...prev, pendingEntry]);
      if (!preserveDraft) {
        setNewMessage("");
      }
      setSending(true);

      try {
        const response = await sendMessageToTherapist(trimmed);
        const sanitized = sanitizeQuickReplyOptions(response?.suggestedReplies);
        setIncomingSuggestions(sanitized);
        return true;
      } catch (err) {
        console.error("Failed to send message to Therapist", err);
        setError(
          err?.message || "We could not reach the Therapist right now. Please try again."
        );
        setPendingMessages((prev) => [
          ...prev.filter((msg) => msg.id !== pendingEntry.id),
          {
            id: `error-${Date.now()}`,
            role: "assistant",
            content:
              "I could not process that message due to a technical issue. Please try again soon.",
            timestamp: new Date(),
            agent: "System",
          },
        ]);
        return false;
      } finally {
        setSending(false);
        setPendingMessages((prev) =>
          prev.filter((msg) => msg.id !== pendingEntry.id)
        );
      }
    },
    [sending, uid, setIncomingSuggestions]
  );

  const handleSend = async (event) => {
    event?.preventDefault();
    if (!newMessage.trim() || sending || !uid) {
      return;
    }
    await sendChatMessage(newMessage);
  };

  const handleQuickReplySelect = useCallback(
    (suggestion) => {
      if (!suggestion) {
        return;
      }
      const preserveDraft = Boolean(newMessage.trim());
      void sendChatMessage(suggestion, { preserveDraft });
    },
    [newMessage, sendChatMessage]
  );

  const handlePlayMessage = useCallback(
    async (message) => {
      if (!message?.id || !message.content) {
        return;
      }

      if (playingMessageId === message.id) {
        audioRef.current?.pause();
        setPlayingMessageId(null);
        return;
      }

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      setLoadingMessageId(message.id);
      setError(null);

      try {
        const url = await fetchTtsUrl(message.content, "alloy");
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => {
          setPlayingMessageId(null);
          audioRef.current = null;
        };
        audio.onerror = () => {
          setPlayingMessageId(null);
          setError("We could not play that message. Please try again later.");
          audioRef.current = null;
        };
        setPlayingMessageId(message.id);
        await audio.play();
      } catch (ttsError) {
        console.error("Failed to play therapist message audio", ttsError);
        setError(
          ttsError?.message ||
            "We could not play that message. Please try again later."
        );
        setPlayingMessageId(null);
        audioRef.current = null;
      } finally {
        setLoadingMessageId(null);
      }
    },
    [playingMessageId, setError]
  );

  const handleInputKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey || sending) {
      return;
    }
    if (event.isComposing || event.nativeEvent?.isComposing) {
      return;
    }
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      handleSend(event);
    }
  };

  const pageBackground = useMemo(() => {
    const base = theme.palette.background.default;
    const primaryGlow = alpha(
      theme.palette.primary.main,
      theme.palette.mode === "light" ? 0.18 : 0.45
    );
    const accentGlow = alpha(accentTone, theme.palette.mode === "light" ? 0.14 : 0.38);
    return `radial-gradient(circle at 0% -20%, ${primaryGlow} 0%, transparent 60%), radial-gradient(circle at 120% -10%, ${accentGlow} 0%, transparent 55%), ${base}`;
  }, [accentTone, theme]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
        py: { xs: 2, sm: 4 },
        px: { xs: 2, sm: 4 },
        background: pageBackground,
        transition: "background 320ms ease",
      }}
    >
      <Box
        sx={{
          flex: 1,
          width: "100%",
          maxWidth: 960,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 3,
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3.5 },
            color: (theme) => (theme.palette.mode === "light" ? theme.palette.text.primary : theme.palette.common.white),
            background: (theme) =>
              theme.palette.mode === "light"
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(accentTone, 0.1)} 40%, ${alpha(theme.palette.background.paper, 0.92)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.4)} 0%, ${alpha(accentTone, 0.32)} 35%, ${alpha(theme.palette.background.paper, 0.72)} 100%)`,
            border: (theme) => `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.24 : 0.45)}`,
            boxShadow: (theme) => theme.shadows[4],
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background: (theme) =>
                theme.palette.mode === "light"
                  ? `radial-gradient(circle at 20% 20%, ${alpha(theme.palette.common.white, 0.4)} 0%, transparent 55%)`
                  : `radial-gradient(circle at 20% 20%, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 0.28)} 0%, transparent 60%)`,
              opacity: 0.9,
            }}
          />
          <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="overline" sx={{ letterSpacing: 1.5, opacity: 0.7 }}>
              AI WELLNESS COMPANION
            </Typography>
            <Typography variant="h4" component="h1">
              Therapist
            </Typography>
            <Typography
              variant="body1"
              sx={{
                maxWidth: 640,
                color:
                  theme.palette.mode === "light"
                    ? theme.palette.text.primary
                    : alpha(theme.palette.common.white, 0.92),
              }}
            >
              Talk with your dedicated AI therapist. Your previous therapist conversations and profile context
              help guide thoughtful, supportive responses tailored to how you feel today. The more detail you share,
              the more precisely they can support your next steps.
            </Typography>
          </Box>
        </Paper>
        {error && <Alert severity="error">{error}</Alert>}
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            borderRadius: 3,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "light" ? 0.6 : 0.5)}`,
            background: (theme) =>
              theme.palette.mode === "light"
                ? alpha(theme.palette.background.paper, 0.92)
                : alpha(theme.palette.background.paper, 0.75),
            backdropFilter: "blur(20px)",
            boxShadow: (theme) => theme.shadows[5],
            overflow: "hidden",
            transition: "background 220ms ease, border 220ms ease",
          }}
        >
          <Box
            ref={listRef}
            sx={{
              flex: 1,
              overflowY: "auto",
              px: { xs: 2.5, md: 3 },
              py: { xs: 2.5, md: 3 },
              display: "flex",
              flexDirection: "column",
              gap: 1.75,
              background: (theme) =>
                theme.palette.mode === "light"
                  ? alpha(theme.palette.primary.main, 0.04)
                  : alpha(theme.palette.primary.main, 0.18),
              transition: "background 220ms ease",
              "&::-webkit-scrollbar": {
                width: 8,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.16),
                borderRadius: 999,
              },
            }}
          >
            {combinedMessages.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 240,
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color:
                      theme.palette.mode === "light"
                        ? alpha(theme.palette.text.primary, 0.65)
                        : alpha(theme.palette.common.white, 0.7),
                  }}
                >
                  Say hello to begin a new conversation with your Therapist. They are ready whenever you are.
                </Typography>
              </Box>
            ) : (
              combinedMessages.map((message) => {
                const isUser = message.role === "user";
                const label = isUser ? "You" : message.agent || "Therapist";
                const displayTime = message.timestamp
                  ? message.timestamp.toLocaleTimeString([], {
                      hour: "numeric",
                      minute: "2-digit",
                    })
                  : "";
                const isPlayingMessage = playingMessageId === message.id;
                const tooltipTitle = isPlayingMessage ? "Stop playback" : "Listen to this message";
                return (
                  <Box
                    key={message.id}
                    sx={{
                      display: "flex",
                      justifyContent: isUser ? "flex-end" : "flex-start",
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: { xs: "85%", sm: "80%" },
                        display: "flex",
                        flexDirection: "column",
                        alignItems: isUser ? "flex-end" : "flex-start",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          letterSpacing: 0.3,
                          color:
                            theme.palette.mode === "light"
                              ? alpha(theme.palette.text.secondary, 0.9)
                              : alpha(theme.palette.common.white, 0.65),
                        }}
                      >
                        {label}
                        {displayTime ? ` • ${displayTime}` : ""}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 0.5,
                          flexDirection: isUser ? "row-reverse" : "row",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            px: { xs: 1.75, sm: 2 },
                            py: { xs: 1.25, sm: 1.5 },
                            borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                            background: (theme) =>
                              isUser
                                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.92 : 0.8)} 0%, ${alpha(theme.palette.primary.dark, theme.palette.mode === "light" ? 0.95 : 0.85)} 100%)`
                                : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.9 : 0.7)} 0%, ${alpha(accentTone, theme.palette.mode === "light" ? 0.12 : 0.26)} 120%)`,
                            color: (theme) =>
                              isUser
                                ? theme.palette.primary.contrastText
                                : theme.palette.mode === "light"
                                ? theme.palette.text.primary
                                : alpha(theme.palette.common.white, 0.92),
                            opacity: message.pending ? 0.75 : 1,
                            border: (theme) =>
                              isUser
                                ? `1px solid ${alpha(theme.palette.primary.dark, 0.5)}`
                                : `1px solid ${alpha(accentTone, theme.palette.mode === "light" ? 0.3 : 0.4)}`,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            boxShadow: (theme) =>
                              isUser ? theme.shadows[6] : theme.shadows[2],
                            transition: "transform 160ms ease, box-shadow 160ms ease",
                            transform: message.pending ? "scale(0.98)" : "scale(1)",
                            "&:hover": {
                              boxShadow: (theme) =>
                                isUser ? theme.shadows[8] : theme.shadows[4],
                            },
                          }}
                        >
                          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                            {message.content}
                          </Typography>
                        </Paper>
                        {!isUser && !message.pending && (
                          <Tooltip title={tooltipTitle}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => void handlePlayMessage(message)}
                                disabled={
                                  Boolean(
                                    loadingMessageId && loadingMessageId !== message.id
                                  )
                                }
                                aria-label={isPlayingMessage ? "Stop playback" : "Listen to therapist message"}
                                sx={{
                                  alignSelf: "center",
                                  backgroundColor: (theme) =>
                                    theme.palette.mode === "light"
                                      ? alpha(
                                          theme.palette.primary.main,
                                          playingMessageId === message.id ? 0.18 : 0.08
                                        )
                                      : alpha(
                                          theme.palette.common.white,
                                          playingMessageId === message.id ? 0.25 : 0.15
                                        ),
                                  "&:hover": {
                                    backgroundColor: (theme) =>
                                      theme.palette.mode === "light"
                                        ? alpha(theme.palette.primary.main, 0.3)
                                        : alpha(theme.palette.common.white, 0.3),
                                  },
                                }}
                              >
                                {loadingMessageId === message.id ? (
                                  <CircularProgress size={18} />
                                ) : isPlayingMessage ? (
                                  <StopRoundedIcon
                                    fontSize="small"
                                    color="primary"
                                  />
                                ) : (
                                  <VolumeUpRoundedIcon
                                    fontSize="small"
                                    color="inherit"
                                  />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
          <Divider sx={{ borderColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.08 : 0.2) }} />
          {quickReplies.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                px: { xs: 2.25, md: 2.5 },
                py: { xs: 1, md: 1.2 },
                background: (theme) =>
                  theme.palette.mode === "light"
                    ? alpha(theme.palette.primary.main, 0.04)
                    : alpha(theme.palette.primary.main, 0.18),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  color: (theme) =>
                    theme.palette.mode === "light"
                      ? alpha(theme.palette.text.secondary, 0.9)
                      : alpha(theme.palette.common.white, 0.7),
                }}
              >
                Prefer tapping? Try a quick reply:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                }}
              >
                {quickReplies.map((option) => (
                  <Button
                    key={option}
                    variant="outlined"
                    size="small"
                    onClick={() => handleQuickReplySelect(option)}
                    disabled={sending || !uid}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 600,
                      letterSpacing: 0.1,
                      px: 1.8,
                      py: 0.4,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "light"
                          ? alpha(theme.palette.common.white, 0.85)
                          : alpha(theme.palette.background.default, 0.5),
                    }}
                  >
                    {option}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
          <Box
            component="form"
            onSubmit={handleSend}
            sx={{
              display: "flex",
              alignItems: { xs: "stretch", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1, sm: 0.75 },
              px: { xs: 2.25, md: 2.5 },
              py: { xs: 1.1, md: 1.6 },
              background: (theme) =>
                theme.palette.mode === "light"
                  ? alpha(theme.palette.background.paper, 0.9)
                  : alpha(theme.palette.background.paper, 0.55),
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="medium"
              placeholder="Share what's on your mind..."
              value={newMessage}
              onChange={(event) => setNewMessage(event.target.value)}
              onKeyDown={handleInputKeyDown}
              disabled={!uid}
              sx={{
                backgroundColor: (theme) =>
                  theme.palette.mode === "light"
                    ? alpha(theme.palette.common.white, 0.9)
                    : alpha(theme.palette.background.default, 0.6),
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  padding: { xs: 0.55, sm: 0.55 },
                  alignItems: "center",
                  minHeight: 48,
                  '& fieldset': {
                    borderColor: alpha(theme.palette.text.primary, 0.12),
                  },
                  '&:hover fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.45),
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: 1.5,
                    borderColor: theme.palette.primary.main,
                  },
                },
                '& .MuiInputBase-input': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  lineHeight: 1.45,
                },
                '& .MuiOutlinedInput-inputMultiline': {
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={sending || !uid || !newMessage.trim()}
              sx={{
                minWidth: { xs: "100%", sm: 130 },
                alignSelf: { xs: "stretch", sm: "center" },
                borderRadius: 99,
                px: { xs: 2, md: 2.75 },
                py: 0,
                height: 48,
                fontWeight: 600,
                letterSpacing: 0.3,
                backgroundImage: (theme) =>
                  theme.palette.mode === "light"
                    ? `linear-gradient(120deg, ${theme.palette.primary.main}, ${accentTone})`
                    : `linear-gradient(120deg, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 0.9)}, ${alpha(accentTone, 0.85)})`,
                boxShadow: (theme) => theme.shadows[6],
                '&:hover': {
                  backgroundImage: (theme) =>
                    theme.palette.mode === "light"
                      ? `linear-gradient(120deg, ${theme.palette.primary.dark}, ${accentTone})`
                      : `linear-gradient(120deg, ${alpha(theme.palette.primary.light ?? theme.palette.primary.main, 1)}, ${alpha(accentTone, 0.95)})`,
                  boxShadow: (theme) => theme.shadows[8],
                },
              }}
            >
              {sending ? <CircularProgress size={20} color="inherit" /> : "SEND"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default TherapistPage;
